import { useState, useEffect } from "react";
import { statusStore } from "@/lib/stores/statusStore";

interface StatusWorkflows {
  templates: Record<string, any>;
  objects: Record<string, any>;
  documents: Record<string, any>;
  dialogs: Record<string, any>;
  loca: Record<string, any>;
}

interface UseStatusWorkflowsProps {
  gameId?: string;
}

/**
 * Find the most relevant status configuration
 * Priority: game-specific > company-level (null game_id) > first available
 */
function findRelevantStatus(statusData: any[], gameId?: string) {
  if (statusData.length === 0) return null;
  
  if (gameId) {
    // First try to find game-specific status
    const gameStatus = statusData.find(s => s.game_id === gameId);
    if (gameStatus) return gameStatus;
    
    // Fall back to company-level status (game_id is null)
    const companyStatus = statusData.find(s => s.game_id === null);
    if (companyStatus) return companyStatus;
  }
  
  // Use first available if no game selected or no match found
  return statusData[0];
}

/**
 * Hook to manage status workflows from the status store
 * Subscribes to status store changes and returns workflows organized by item type
 */
export function useStatusWorkflows({ gameId }: UseStatusWorkflowsProps): StatusWorkflows {
  const [statusWorkflows, setStatusWorkflows] = useState<StatusWorkflows>({
    templates: {},
    objects: {},
    documents: {},
    dialogs: {},
    loca: {},
  });

  useEffect(() => {
    const updateWorkflows = (statusData: any[]) => {
      const relevantStatus = findRelevantStatus(statusData, gameId);
      
      if (relevantStatus) {
        setStatusWorkflows({
          templates: relevantStatus.templates || {},
          objects: relevantStatus.objects || {},
          documents: relevantStatus.documents || {},
          dialogs: relevantStatus.dialogs || {},
          loca: relevantStatus.loca || {},
        });
      }
    };

    const subscription = statusStore.subscribe(() => {
      const statusData = statusStore.getData();
      updateWorkflows(statusData);
    });

    // Initial load
    const statusData = statusStore.getData();
    updateWorkflows(statusData);

    return () => {
      subscription();
    };
  }, [gameId]);

  return statusWorkflows;
}

